import React, { useState, useEffect } from 'react';
import { moveEnvelopeFiles, getEnvelopeFolderList } from '../../../../../services/CommonService';
import { toast } from 'react-toastify';
import { Modal, Button, Form } from 'react-bootstrap';

const MoveFilesComponent = ({ show, onHide, envelopeId, mainFolderName, files, onMoveSuccess, onRefreshDocuments }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [targetFolder, setTargetFolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    if (show && envelopeId) {
      fetchFolders();
      setSelectedFiles(files?.length === 1 ? [files[0].id] : []);
    }
  }, [show, envelopeId, files]);

  const fetchFolders = async () => {
    try {
      const { data } = await getEnvelopeFolderList({ envelope_id: Number(envelopeId) });
      setFolders(data?.folders || []);
    } catch (error) {
      // Check if the error message is "No folders found for this envelope"
      if (error?.response?.data?.message === 'No folders found for this envelope') {
        toast.error('No folders available to move this file.');
      } else {
        toast.error('Failed to fetch folders');
      }
      setFolders([]);
    }
  };

  const availableFolders = folders.filter(f => f && f !== mainFolderName);

  const handleMove = async () => {
    if (!envelopeId || !selectedFiles.length || !targetFolder) {
      toast.error('Please select files and a target folder.');
      return;
    }

    setLoading(true);
    try {
      await moveEnvelopeFiles({
        envelope_id: Number(envelopeId),
        file_ids: selectedFiles,
        target_folder_name: targetFolder
      });

      toast.success('Files moved successfully!');
      if (onMoveSuccess && files[0]) {
        onMoveSuccess({
          targetFolder,
          movedFileId: files[0].id,
          movedFile: {
            ...files[0],
            type: 'file',
            name: files[0].file_name,
            file_name: files[0].file_name,
            id: files[0].id,
            file_path: files[0].file_path,
            folder_name: targetFolder
          },
          refresh: true
        });
      }

      setSelectedFiles([]);
      setTargetFolder('');
      setLoading(true);
      onHide();

      if (onRefreshDocuments) {
        onRefreshDocuments();
      }
    } catch {
      toast.error('Failed to move files.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header>
        <Modal.Title>Move Documents</Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={onHide}><i className="bi bi-x-circle" aria-hidden="true"></i></button>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="page-loading"><img src="/images/loader.gif" alt="loader" /></div>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Document</Form.Label>
            <div className="border rounded p-2">
              {files && (files[0]?.file_name || files[0]?.name) || 'No file selected'}
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Target Folder</Form.Label>
            <Form.Select value={targetFolder} onChange={(e) => setTargetFolder(e.target.value)}>
              <option value="" disabled>Select a folder</option>
              {availableFolders.map(folderName => (
                <option key={folderName} value={folderName}>{folderName}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleMove}
          disabled={loading || !selectedFiles.length || !targetFolder}
        >
          {loading ? 'Moving...' : 'Move'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveFilesComponent;
